import type { Destination, FlowPieceKind } from '@arcnet/contracts';
import { KIND_LABEL, type Body, type Live } from './model.js';

/**
 * A version's changes in words (docs/41 §12, Fable's wording table): one line per setting, the
 * setting as it is in that version and, muted, what it was. Pure, so the list, the confirm and the
 * tests all read the same words.
 */

export interface Line { text: string; was?: string | undefined }

/** The names a description needs, from the account's live lists and the version's own steps. */
export interface Names {
  ext: (id: string) => string | null;
  box: (id: string) => string | null;
  media: (id: string) => string | null;
  schedule: (id: string) => string | null;
  businessId: string | null;
  piece: (kind: FlowPieceKind, id: string) => string | null;
  number: (id: string) => { e164: string; routeName: string | null } | null;
}

export function namesFrom(live: Live, steps: ReadonlyArray<{ kind: FlowPieceKind; id: string; body: Body | null }> = []): Names {
  const ext = new Map(live.extensions.map((e) => [e.id, `${e.number} ${e.display_name}`]));
  const box = new Map(live.boxes.map((b) => [b.id, `${b.extension_number} ${b.extension_name}`]));
  const media = new Map(live.media.map((m) => [m.id, m.duration_sec ? `${m.name} (${m.duration_sec.toFixed(1)} s)` : m.name]));
  const schedule = new Map(live.schedules.map((s) => [s.id, s.name]));
  const pieces = new Map<string, string>();
  const lists: Array<[FlowPieceKind, Array<{ id: string; name: string }>]> = [
    ['inbound_route', live.routes], ['ivr', live.ivrs], ['ring_group', live.groups], ['queue', live.queues],
    ['message', live.messages], ['hours', live.hours],
  ];
  for (const [kind, xs] of lists) for (const x of xs) pieces.set(`${kind}:${x.id}`, x.name);
  // A step's name in the version wins: it may have been renamed or deleted since.
  for (const s of steps) if (s.body && typeof s.body.name === 'string') pieces.set(`${s.kind}:${s.id}`, s.body.name);
  const numbers = new Map(live.numbers.map((n) => [n.id, n.e164]));
  const routeOf = new Map(live.routes.flatMap((r) => r.numbers.map((n) => [n.id, r.name] as const)));
  return {
    ext: (id) => ext.get(id) ?? null,
    box: (id) => box.get(id) ?? null,
    media: (id) => media.get(id) ?? null,
    schedule: (id) => schedule.get(id) ?? null,
    businessId: live.businessId,
    piece: (kind, id) => pieces.get(`${kind}:${id}`) ?? null,
    number: (id) => (numbers.has(id) ? { e164: numbers.get(id)!, routeName: routeOf.get(id) ?? null } : null),
  };
}

const KIND_OF_DEST: Record<string, FlowPieceKind> = { ivr: 'ivr', ring_group: 'ring_group', queue: 'queue', message: 'message', hours: 'hours' };

/** Where an exit sends the call, as the destination picker names it. */
export function destText(d: unknown, n: Names, none = 'Hang up'): string {
  if (!d || typeof d !== 'object') return none;
  const x = d as Destination;
  switch (x.type) {
    case 'hangup': return 'Hang up';
    case 'external': return x.number;
    case 'extension': return n.ext(x.id) ?? 'an extension since deleted';
    case 'voicemail': { const b = n.box(x.id); return b ? `Voicemail ${b}` : 'a voicemail box since deleted'; }
    default: {
      const kind = KIND_OF_DEST[x.type];
      if (!kind) return 'somewhere unknown';
      const name = n.piece(kind, (x as { id: string }).id);
      return name ? `${KIND_LABEL[kind]} · ${name}` : `a ${KIND_LABEL[kind].toLowerCase()} since deleted`;
    }
  }
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
const str = (x: unknown) => (typeof x === 'string' && x !== '' ? x : null);
const onOff = (x: unknown) => (x === true ? 'on' : 'off');
const recording = (id: unknown, n: Names, none = 'none') => { const s = str(id); return s ? n.media(s) ?? 'a recording since deleted' : none; };
const secs = (x: unknown) => `${Number(x ?? 0)} s`;
const mins = (x: unknown) => { const s = Number(x ?? 0); return s % 60 === 0 ? `${s / 60} min` : `${s} s`; };
const times = (k: number) => `${k} ${k === 1 ? 'time' : 'times'}`;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
function daysText(ds: number[]): string {
  const s = [...new Set(ds)].sort((a, b) => a - b);
  if (s.length === 7) return 'Every day';
  const run = s.length > 2 && s.every((d, i) => i === 0 || d === s[i - 1]! + 1);
  return run ? `${DAYS[s[0]!]}–${DAYS[s[s.length - 1]!]}` : s.map((d) => DAYS[d]).join(', ');
}
/** Hours as a person reads them: always, a named schedule, or its own hours in brief. */
export function hoursText(h: unknown, n: Names): string {
  const x = (h ?? { mode: 'always' }) as { mode?: string; schedule_id?: string; rules?: Body[] };
  if (x.mode === 'always' || !x.mode) return 'Always open';
  if (x.mode === 'schedule') {
    const name = x.schedule_id ? n.schedule(x.schedule_id) ?? 'a schedule since deleted' : 'no schedule';
    return x.schedule_id && x.schedule_id === n.businessId ? `Business hours (${name})` : name;
  }
  const open = (x.rules ?? []).filter((r) => r.is_open !== false && (r.kind ?? 'weekly') === 'weekly');
  if (open.length === 0) return 'Its own hours';
  const parts = open.slice(0, 2).map((r) => `${daysText((r.days_of_week as number[]) ?? [])} ${str(r.time_from) ?? ''}–${str(r.time_to) ?? ''}`.trim());
  return `Its own hours (${parts.join('; ')}${open.length > 2 ? '; …' : ''})`;
}

const RING: Record<string, string> = { simultaneous: 'everyone at once', sequential: 'one after another', random: 'in a random order' };
const QUEUE: Record<string, string> = {
  longest_idle: 'the agent free longest', round_robin: 'each agent in turn', ring_all: 'every free agent at once',
  top_down: 'agents in list order', agent_with_least_talk_time: 'the agent with least talk time', random: 'a random free agent',
};

/** Who is in a list, added and removed, then whether only the order changed. */
function people(label: string | null, was: string[], now: string[], name: (id: string) => string): Line[] {
  const out: Line[] = [];
  const added = now.filter((x) => !was.includes(x));
  const removed = was.filter((x) => !now.includes(x));
  const pre = label ? `${label}: ` : '';
  if (added.length + removed.length > 3) {
    out.push({ text: `${pre}${now.length} ${now.length === 1 ? 'person' : 'people'}`, was: `${was.length}` });
  } else {
    for (const a of added) out.push({ text: `${pre}${name(a)} added` });
    for (const r of removed) out.push({ text: `${pre}${name(r)} removed` });
  }
  if (added.length === 0 && removed.length === 0 && !same(was, now)) out.push({ text: `${pre}order changed` });
  return out;
}
const memberKey = (m: Body) => (str(m.extension_id) ? `e:${m.extension_id as string}` : `x:${String(m.external_number ?? '')}`);

/** What a new step is, in one line. */
function newStep(kind: FlowPieceKind, b: Body, n: Names): Line[] {
  switch (kind) {
    case 'message': return [{ text: `Plays ${recording(b.media_id, n, 'no recording yet')}, then ${destText(b.next_destination, n).replace(/^Hang up$/, 'hangs up')}` }];
    case 'hours': return [{ text: `${hoursText(b.hours, n)}: open goes to ${destText(b.open_destination, n)}, closed to ${destText(b.closed_destination, n)}` }];
    case 'ivr': {
      const k = ((b.options as Body[] | undefined) ?? []).length;
      return [{ text: `${k} ${k === 1 ? 'key' : 'keys'}, greeting ${recording(b.greeting_media_id, n)}` }];
    }
    case 'ring_group': {
      const steps = (b.steps as Body[] | undefined) ?? [];
      const ppl = steps.reduce((s, x) => s + ((x.members as Body[] | undefined) ?? []).length, 0);
      return [{ text: `Rings ${ppl} ${ppl === 1 ? 'person' : 'people'} ${RING[String(steps[0]?.strategy ?? 'simultaneous')] ?? ''} for ${secs(steps[0]?.ring_sec)}` }];
    }
    case 'queue': {
      const k = ((b.agents as Body[] | undefined) ?? []).length;
      return [{ text: `${k} ${k === 1 ? 'agent' : 'agents'}, offers calls to ${QUEUE[String(b.strategy)] ?? String(b.strategy)}` }];
    }
    case 'inbound_route': return [{ text: `New route, calls go to ${destText(b.destination, n, 'nowhere yet')}` }];
  }
}

/**
 * The lines for one step: what changed in it, from `before` to `after`. A new step is one line
 * saying what it is; a deleted one says so.
 */
export function describeStep(kind: FlowPieceKind, before: Body | null, after: Body | null, n: Names): Line[] {
  if (!after) return [{ text: 'Deleted' }];
  if (!before) return newStep(kind, after, n);
  const b = before, a = after;
  const out: Line[] = [];
  const changed = (k: string) => !same(b[k], a[k]);
  const line = (text: string, was?: string) => out.push(was === undefined ? { text } : { text, was });

  if (changed('name')) line(`Name: ${String(a.name ?? '')}`, String(b.name ?? ''));
  if (changed('extension')) line(`Dial code ${str(a.extension) ?? 'none'}`, str(b.extension) ?? 'none');

  switch (kind) {
    case 'inbound_route': {
      const was = (b.number_ids as string[] | undefined) ?? [], now = (a.number_ids as string[] | undefined) ?? [];
      for (const id of now.filter((x) => !was.includes(x))) line(`${n.number(id)?.e164 ?? 'A number'} added`);
      for (const id of was.filter((x) => !now.includes(x))) {
        const num = n.number(id);
        line(`${num?.e164 ?? 'A number'} removed${num?.routeName ? ` · now on ${num.routeName}` : ''}`);
      }
      if (changed('hours')) line(`Hours: ${hoursText(a.hours, n)}`, hoursText(b.hours, n));
      const split = ((a.hours as { mode?: string } | undefined)?.mode ?? 'always') !== 'always';
      if (changed('destination')) line(`${split ? 'Open' : 'Calls go to'}: ${destText(a.destination, n, 'nowhere')}`, destText(b.destination, n, 'nowhere'));
      if (changed('closed_destination') && (split || a.closed_destination)) line(`Closed: ${destText(a.closed_destination, n)}`, destText(b.closed_destination, n));
      if (changed('cid_name_prefix')) line(`Prepend to caller ID: ${str(a.cid_name_prefix) ?? 'none'}`, str(b.cid_name_prefix) ?? 'none');
      if (changed('is_enabled')) line(`Taking calls: ${onOff(a.is_enabled !== false)}`, onOff(b.is_enabled !== false));
      break;
    }
    case 'ivr': {
      if (changed('greeting_media_id')) line(`Greeting ${recording(a.greeting_media_id, n)}`, recording(b.greeting_media_id, n));
      if (changed('invalid_media_id')) line(`Wrong-key recording: ${recording(a.invalid_media_id, n)}`, recording(b.invalid_media_id, n));
      if (changed('timeout_sec')) line(`Waits ${secs(a.timeout_sec)} for a key`, secs(b.timeout_sec));
      if (changed('digit_timeout_sec')) line(`Between digits ${secs(a.digit_timeout_sec)}`, secs(b.digit_timeout_sec));
      if (changed('max_attempts')) line(`Plays up to ${times(Number(a.max_attempts))}`, String(b.max_attempts));
      if (changed('answer_after_rings')) line(`Rings ${times(Number(a.answer_after_rings ?? 0))} before answering`, String(b.answer_after_rings ?? 0));
      if (changed('direct_dial')) line(`Direct dialling: ${onOff(a.direct_dial)}`, onOff(b.direct_dial));
      const keys = (x: Body) => new Map(((x.options as Body[] | undefined) ?? []).map((o) => [String(o.digit), o]));
      const kb = keys(b), ka = keys(a);
      for (const d of [...new Set([...kb.keys(), ...ka.keys()])].sort()) {
        const was = kb.get(d), now = ka.get(d);
        if (now && !was) line(`Key ${d}${str(now.label) ? ` “${now.label as string}”` : ''} added, goes to ${destText(now.destination, n)}`);
        else if (was && !now) line(`Key ${d} removed`, `it went to ${destText(was.destination, n)}`);
        else if (was && now) {
          if (!same(was.destination, now.destination)) line(`Key ${d} goes to ${destText(now.destination, n)}`, destText(was.destination, n));
          if (!same(str(was.label), str(now.label))) line(`Key ${d} is called “${str(now.label) ?? ''}”`, str(was.label) ? `“${was.label as string}”` : 'nothing');
        }
      }
      if (changed('timeout_destination')) line(`No key goes to ${destText(a.timeout_destination, n)}`, destText(b.timeout_destination, n));
      if (changed('invalid_destination')) {
        line(`Wrong key goes to ${a.invalid_destination ? destText(a.invalid_destination, n) : 'the same place as no key'}`,
          b.invalid_destination ? destText(b.invalid_destination, n) : 'the same place as no key');
      }
      break;
    }
    case 'ring_group': {
      const sb = (b.steps as Body[] | undefined) ?? [], sa = (a.steps as Body[] | undefined) ?? [];
      const many = sb.length > 1 || sa.length > 1;
      const who = (k: string) => (k.startsWith('e:') ? n.ext(k.slice(2)) ?? 'an extension since deleted' : k.slice(2));
      for (let i = 0; i < Math.max(sb.length, sa.length); i++) {
        const was = sb[i], now = sa[i];
        const pre = many ? `Step ${i + 1}: ` : '';
        if (now && !was) { line(`Step ${i + 1} added: rings ${((now.members as Body[] | undefined) ?? []).length} for ${secs(now.ring_sec)}`); continue; }
        if (was && !now) { line(`Step ${i + 1} removed`); continue; }
        if (!was || !now) continue;
        if (!same(was.strategy, now.strategy)) line(`${pre}Rings ${RING[String(now.strategy)] ?? now.strategy}`, RING[String(was.strategy)] ?? String(was.strategy));
        if (!same(was.ring_sec, now.ring_sec)) line(`${pre}Rings ${now.strategy === 'simultaneous' ? 'for' : 'each for'} ${secs(now.ring_sec)}`, secs(was.ring_sec));
        if (!same(str(was.prompt_media_id), str(now.prompt_media_id))) line(`${pre}Plays ${recording(now.prompt_media_id, n)} first`, recording(was.prompt_media_id, n));
        out.push(...people(many ? `Step ${i + 1}` : null,
          ((was.members as Body[] | undefined) ?? []).map(memberKey), ((now.members as Body[] | undefined) ?? []).map(memberKey), who));
      }
      if (changed('skip_busy')) line(`Skip busy phones: ${onOff(a.skip_busy !== false)}`, onOff(b.skip_busy !== false));
      if (changed('outside_extension_id')) {
        const o = (x: unknown) => (str(x) ? n.ext(x as string) ?? 'an extension since deleted' : 'none');
        line(`Outside numbers are rung as ${o(a.outside_extension_id)}`, o(b.outside_extension_id));
      }
      if (changed('timeout_destination')) line(`No answer goes to ${destText(a.timeout_destination, n)}`, destText(b.timeout_destination, n));
      break;
    }
    case 'queue': {
      if (changed('strategy')) line(`Offer calls to ${QUEUE[String(a.strategy)] ?? a.strategy}`, QUEUE[String(b.strategy)] ?? String(b.strategy));
      const ag = (x: Body) => ((x.agents as Body[] | undefined) ?? [])
        .slice().sort((p, q) => Number(p.tier_level) - Number(q.tier_level) || Number(p.tier_position) - Number(q.tier_position))
        .map((m) => String(m.extension_id));
      out.push(...people('Agents', ag(b), ag(a), (id) => n.ext(id) ?? 'an extension since deleted'));
      if (changed('moh_media_id')) line(`Music: ${recording(a.moh_media_id, n, 'the account’s hold music')}`, recording(b.moh_media_id, n, 'the account’s hold music'));
      if (changed('periodic_media_id')) line(`Message while waiting: ${recording(a.periodic_media_id, n)}`, recording(b.periodic_media_id, n));
      if (changed('periodic_interval_sec') && str(a.periodic_media_id)) line(`Message every ${secs(a.periodic_interval_sec)}`, secs(b.periodic_interval_sec));
      if (changed('announce_position')) line(`Tell callers their place in the queue: ${onOff(a.announce_position)}`, onOff(b.announce_position));
      if (changed('announce_interval_sec') && a.announce_position) line(`Repeat every ${secs(a.announce_interval_sec)}`, secs(b.announce_interval_sec));
      if (changed('exit_key') || changed('exit_destination')) {
        const ex = (x: Body) => (str(x.exit_key) ? `${x.exit_key as string}, goes to ${destText(x.exit_destination, n)}` : 'none');
        line(`Key to leave the queue: ${ex(a)}`, ex(b));
      }
      if (changed('max_wait_sec')) line(`Wait at most ${mins(a.max_wait_sec)}`, mins(b.max_wait_sec));
      if (changed('timeout_destination')) line(`After the wait, goes to ${destText(a.timeout_destination, n)}`, destText(b.timeout_destination, n));
      if (changed('max_waiting_calls')) line(`At most ${String(a.max_waiting_calls)} callers waiting`, String(b.max_waiting_calls));
      if (changed('wrapup_sec')) line(`Wrap-up ${secs(a.wrapup_sec)}`, secs(b.wrapup_sec));
      if (changed('require_disposition')) line(`Agents must mark a call: ${onOff(a.require_disposition)}`, onOff(b.require_disposition));
      if (changed('sticky_agent_days')) {
        const st = (x: unknown) => (x ? `on (${String(x)} days)` : 'off');
        line(`Send a caller back to their last agent: ${st(a.sticky_agent_days)}`, st(b.sticky_agent_days));
      }
      if (changed('sticky_ring_sec') && a.sticky_agent_days) line(`Ring their last agent for ${secs(a.sticky_ring_sec)}`, secs(b.sticky_ring_sec));
      if (changed('workspace_controls')) {
        const wb = (b.workspace_controls ?? {}) as Body, wa = (a.workspace_controls ?? {}) as Body;
        for (const k of [...new Set([...Object.keys(wb), ...Object.keys(wa)])].sort()) {
          if (same(wb[k], wa[k])) continue;
          const v = (x: unknown) => (x === undefined || x === null ? 'account default' : typeof x === 'boolean' ? onOff(x) : String(x));
          line(`Workspace · ${k.replace(/_/g, ' ')}: ${v(wa[k])}`, v(wb[k]));
        }
      }
      break;
    }
    case 'message': {
      if (changed('media_id')) line(`Recording ${recording(a.media_id, n)}`, recording(b.media_id, n));
      if (changed('next_destination')) line(`Then: ${destText(a.next_destination, n)}`, destText(b.next_destination, n));
      break;
    }
    case 'hours': {
      if (changed('hours')) line(`Hours: ${hoursText(a.hours, n)}`, hoursText(b.hours, n));
      if (changed('open_destination')) line(`Open: ${destText(a.open_destination, n)}`, destText(b.open_destination, n));
      if (changed('closed_destination')) line(`Closed: ${destText(a.closed_destination, n)}`, destText(b.closed_destination, n));
      break;
    }
  }
  return out;
}

/** A line as one string, for summaries and titles. */
export const lineText = (l: Line) => (l.was !== undefined ? `${l.text}, was ${l.was}` : l.text);
